import React, { useCallback, useRef, useState, useEffect } from 'react';
import { APIEndpoint, APIClient } from '../../lib/api_client';
import { usePlan } from '../../hooks/PlanProvider';
import { useQuery } from '@tanstack/react-query';
import { Activity, CustomPlace, PlannerMapPlace, PlannerLineData, getLatLng, getLng, getLat } from '../../types/interfaces';
import Path from './Path';
import ActivityDrawer from '../ActivityDrawer/ActivityDrawer';
import Cookies from 'js-cookie';
import { MapMarker, MemoizedMapMarker } from './MapMarker';
import './Map.scss';
import './MapSidebar.scss';
import { ActivityTypes, FilterOptions, FilterPanel } from './FilterPanel';
import { DEFAULT_LOCATION, useGeolocation } from '../../hooks/GeolocationProvider';
import PlanStepperBase from '../PlanStepper/PlanStepperBase';
import PlanStepperHeader from '../PlanStepper/PlanStepperHeader';
import { useMaps } from 'src/hooks/MapProvider';
import BaseMap from './BaseMap';
import SearchModeToggle, { SearchMode } from './SearchModeToggle';
import { IconButton, Tooltip, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faWandMagicSparkles, faSearch, faTimes, faUndo } from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from '../../hooks/SnackbarProvider';
import { keysToCamel } from '../../lib/utils';
import { v4 as uuidv4 } from 'uuid';
import { MapSidebar } from './MapSidebar';
import { LocationCard } from '../InteractiveMapPlanner/LocationCard';
import '../InteractiveMapPlanner/InteractiveMapPlanner.scss';
import { MapAnalytics } from '../../lib/analytics';
import { useSearchTracking } from '../../hooks/usePerformanceTracking';

const MAP_DEFAULT_ZOOM = 16;

export const MAP_DEFAULT_FILTER_OPTIONS: FilterOptions = {
  activityTypes: Object.values(ActivityTypes),
  priceRange: [0, 4],
  ambiance: [],
  distance: 5000
};


const Map: React.FC = () => {
  const { userGeolocation } = useGeolocation();
  const { selectedPlan } = usePlan();
  const { mapsClient } = useMaps();
  const { triggerSnackbar: showSnackbar } = useSnackbar();
  const { trackNoResults } = useSearchTracking();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentActivity, setCurrentActivity] = useState<Activity | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>(MAP_DEFAULT_FILTER_OPTIONS);
  const [isCollapsed, setIsCollapsed] = useState(selectedPlan.activities.length === 0);

  // Search mode state
  const [searchMode, setSearchMode] = useState<SearchMode>(SearchMode.Places);
  const [aiGeneratedLocations, setAiGeneratedLocations] = useState<PlannerMapPlace[]>([]);
  const [aiGeneratedLines, setAiGeneratedLines] = useState<PlannerLineData[]>([]);

  // AI mode specific state
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [isDesktopView, setIsDesktopView] = useState<boolean>(window.innerWidth > 768);
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [promptText, setPromptText] = useState<string>('');

  // Places search state
  const [placesSearchText, setPlacesSearchText] = useState<string>('');
  const [placesSearchResults, setPlacesSearchResults] = useState<CustomPlace[]>([]);
  const [isPlacesSearching, setIsPlacesSearching] = useState<boolean>(false);
  const [showPlacesResults, setShowPlacesResults] = useState<boolean>(false);

  // Refs
  const mapRef = useRef<google.maps.Map | null>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const timelineScrollAreaRef = useRef<HTMLDivElement>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (selectedPlan.activities.length === 0) {
      setIsCollapsed(true)
    }
  }, [selectedPlan.activities]);

  // Effect to handle window resize for responsive search bar
  useEffect(() => {
    const handleResize = () => {
      setIsDesktopView(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize Places service
  useEffect(() => {
    if (window.google && window.google.maps && !placesServiceRef.current) {
      const mapDiv = document.createElement('div');
      const map = new google.maps.Map(mapDiv);
      placesServiceRef.current = new google.maps.places.PlacesService(map);
    }
  }, []);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    // Add bounds change listener with debouncing
    let boundsTimeout: NodeJS.Timeout;
    map.addListener('bounds_changed', () => {
      clearTimeout(boundsTimeout);
      boundsTimeout = setTimeout(() => {
        const bounds = map.getBounds();
        const center = map.getCenter();
        const zoom = map.getZoom();
        
        // Track map bounds change for analytics
        if (bounds && center) {
          MapAnalytics.trackEvent('map_bounds_change', {
            event_category: 'map_interaction',
            zoom_level: zoom,
            latitude: center.lat(),
            longitude: center.lng(),
            event_label: `map_moved_zoom_${zoom}`
          });
        }
        
        setMapBounds(bounds || null);
      }, 300); // 300ms debounce
    });
  }, []);

  const fetchMarkers = async (): Promise<CustomPlace[]> => {
    const response = await APIClient.get(`${APIEndpoint}/markers/`, {
      headers: { Accept: 'application/json' },
      params: userGeolocation || DEFAULT_LOCATION
    });
    return response.data || [];
  };

  const fetchMarkersQuery = useQuery<CustomPlace[]>({
    queryKey: ['fetchFilteredMarkers'],
    queryFn: fetchMarkers,
    enabled: !!Cookies.get("mindara_at"),
    retry: false
  });

  const fetchFilteredMarkers = useCallback((): CustomPlace[] => {
    let filteredActivities = fetchMarkersQuery?.data?.filter((activity) => {
      const filterConditions = [
        () => activity?.types?.some((type) => filters.activityTypes.includes(type)),
        // If the price_level is undefined, we allow it by usage of infinity
        () => (activity?.price_level || Infinity) >= filters.priceRange[0],
        () => (activity?.price_level || -Infinity) <= filters.priceRange[1]
      ];
      return filterConditions.every((condition) => condition());
    }) || [];

    // Filter to only show markers within viewport bounds
    if (mapBounds && filteredActivities.length > 0) {
      filteredActivities = filteredActivities.filter((activity) => {
        const position = new google.maps.LatLng(getLat(activity), getLng(activity));
        return mapBounds.contains(position);
      });
    }

    // Limit to 25 closest markers to improve performance
    if (filteredActivities.length > 35) {
      const userLocation = userGeolocation || DEFAULT_LOCATION;

      // Sort by distance from user location
      filteredActivities.sort((a, b) => {
        const distanceA = google.maps.geometry?.spherical?.computeDistanceBetween(
          new google.maps.LatLng(userLocation.lat, userLocation.lng),
          new google.maps.LatLng(getLat(a), getLng(a))
        ) || 0;
        const distanceB = google.maps.geometry?.spherical?.computeDistanceBetween(
          new google.maps.LatLng(userLocation.lat, userLocation.lng),
          new google.maps.LatLng(getLat(b), getLng(b))
        ) || 0;
        return distanceA - distanceB;
      });

      // Take only the closest 25
      filteredActivities = filteredActivities.slice(0, 35);
    }

    return filteredActivities;
  }, [fetchMarkersQuery?.data, filters, mapBounds, userGeolocation]);

  const handleMarkerClick = (place: CustomPlace) => {
    MapAnalytics.markerClick(
      place.placeId || 'unknown',
      place.name || 'Unknown Place',
      place.types?.[0] || 'unknown'
    );
    MapAnalytics.drawerOpen('marker', place.name || 'Unknown Place');
    setCurrentActivity(place as Activity);
    setDrawerOpen(true);
  };

  // Toggle search mode
  const handleSearchModeChange = useCallback((mode: SearchMode) => {
    // Track search mode toggle
    MapAnalytics.trackEvent('map_search_mode_toggle', {
      event_category: 'map_interaction',
      previous_mode: searchMode,
      new_mode: mode,
      event_label: `search_mode_${mode}`
    });
    
    setSearchMode(mode);

    // Clear AI-generated results when switching away from AI mode
    if (mode !== SearchMode.AI) {
      setAiGeneratedLocations([]);
      setAiGeneratedLines([]);
      setActiveLocationId(null);
      setShowTimeline(false);
      setShowSidebar(false);
      setPromptText('');
      setIsLoading(false);
      setIsStreaming(false);
    } else {
      // Show sidebar when switching to AI mode
      setShowSidebar(true);
    }
  }, [searchMode]);

  // Clear AI results
  const clearAIResults = useCallback(() => {
    setAiGeneratedLocations([]);
    setAiGeneratedLines([]);
  }, []);

  // Stream AI planner functionality
  const streamInteractiveMapPlan = (prompt: string): EventSource => {
    const queryParams = new URLSearchParams({ prompt });
    let url = `${APIEndpoint}/ai/interactive-map-stream/?${queryParams.toString()}`;
    return new EventSource(url, { withCredentials: true });
  };

  // Handle location selection from AI results
  const handleLocationSelect = useCallback((locationData: PlannerMapPlace) => {
    setActiveLocationId(locationData.placeId);
    const coordinates = getLatLng(locationData);
    if (mapRef.current && coordinates.lat && coordinates.lng) {
      mapRef.current.panTo({ lat: coordinates.lat, lng: coordinates.lng });
    }

    const activityForDrawer: Activity = {
      ...locationData,
      name: locationData.name || '',
      description: locationData.description,
      notes: locationData.notes || locationData.description,
      formatted_address: locationData.formatted_address || `Lat: ${coordinates.lat.toFixed(4)}, Lng: ${coordinates.lng.toFixed(4)}`,
      photos: locationData.photos || [],
      rating: locationData.rating,
      website: locationData.website,
      phone_number: locationData.phone_number,
      placeId: locationData.placeId,
      type: 'ai_suggestion',
    };
    setCurrentActivity(activityForDrawer);
    setDrawerOpen(true);

    const cardIndex = aiGeneratedLocations.findIndex(loc => loc.placeId === locationData.placeId);
    if (cardIndex !== -1 && cardContainerRef.current) {
      const cardElement = cardContainerRef.current.children[cardIndex] as HTMLElement;
      if (cardElement) cardElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [aiGeneratedLocations]);

  // Handle AI prompt submission
  const handleSendPrompt = useCallback(async () => {
    if (!promptText.trim()) return;
    setIsLoading(true);
    setIsStreaming(true);
    setAiGeneratedLocations([]);
    setAiGeneratedLines([]);
    setActiveLocationId(null);

    const currentEventSource = streamInteractiveMapPlan(promptText);

    currentEventSource.onmessage = (event) => {
      const parsedData = keysToCamel(JSON.parse(event.data));

      if (parsedData.type === 'location') {
        const locArgs = parsedData.data;
        if (!locArgs.placeId) return;

        const newLocation: PlannerMapPlace = {
          placeId: locArgs.placeId,
          name: locArgs.name,
          description: locArgs.description,
          time: locArgs.time,
          duration: locArgs.duration,
          sequence: locArgs.sequence,
          formatted_address: locArgs.address || '',
          photos: locArgs.photoReferences || [],
          htmlAttributions: locArgs.htmlAttributions || [],
          rating: locArgs.rating,
          website: locArgs.website,
          phone_number: locArgs.phoneNumber,
          notes: locArgs.notes,
          position: new window.google.maps.LatLng(Number(locArgs.lat), Number(locArgs.lng)),
          geometry: {
            location: new window.google.maps.LatLng(Number(locArgs.lat), Number(locArgs.lng))
          }
        };

        setAiGeneratedLocations(prev => {
          if (prev.find(p => p.placeId === newLocation.placeId)) return prev;
          const newLocations = [...prev, newLocation];
          // Show sidebar when we have AI-generated locations
          if (newLocations.length > 0) {
            setShowSidebar(true);
          }
          return newLocations;
        });
      } else if (parsedData.type === 'line') {
        const lineArgs = parsedData.data;
        const newLine: PlannerLineData = {
          id: lineArgs.id || uuidv4(),
          name: lineArgs.name,
          start_location_id: lineArgs.start_location_id,
          end_location_id: lineArgs.end_location_id,
          transport: lineArgs.transport_mode || lineArgs.transport,
          travelTime: lineArgs.travel_time || lineArgs.travelTime,
        };
        setAiGeneratedLines(prev => [...prev, newLine]);
      } else if (parsedData.type === 'error') {
        setIsLoading(false);
        setIsStreaming(false);
        currentEventSource.close();
        showSnackbar(parsedData.message || 'Streaming error.', 5000, 'error');
      } else if (parsedData.type === 'done') {
        setIsLoading(false);
        setIsStreaming(false);
        currentEventSource.close();
        showSnackbar('Plan generation complete!', 3000, 'success');
      }
    };

    currentEventSource.onerror = (errorEvent) => {
      console.error("EventSource failed during stream:", errorEvent);
      setIsLoading(false);
      setIsStreaming(false);
      showSnackbar('Connection error during streaming.', 5000, 'error');
      currentEventSource.close();
    };
  }, [promptText, showSnackbar]);

  // Reset AI results and map
  const handleResetAI = useCallback(() => {
    setPromptText('');
    setAiGeneratedLocations([]);
    setAiGeneratedLines([]);
    setActiveLocationId(null);
    setIsLoading(false);
    setIsStreaming(false);
    if (mapRef.current) {
      const defaultCenter = { lat: 34.0522, lng: -118.2437 };
      mapRef.current.setCenter(defaultCenter);
      mapRef.current.setZoom(10);
    }
    setShowTimeline(false);
  }, []);

  // Debounced places search
  const searchPlaces = useCallback((query: string) => {
    if (!placesServiceRef.current || !query.trim()) {
      setPlacesSearchResults([]);
      setShowPlacesResults(false);
      return;
    }

    setIsPlacesSearching(true);

    const request: google.maps.places.TextSearchRequest = {
      query,
      location: userGeolocation || DEFAULT_LOCATION,
      radius: 50000, // 50km radius
    };

    placesServiceRef.current.textSearch(request, (results, status) => {
      setIsPlacesSearching(false);

      if (status === google.maps.places.PlacesServiceStatus.OK && results) {
        const places: CustomPlace[] = results
          .filter(place => place.place_id && place.geometry?.location)
          .slice(0, 5) // Limit to 5 results for inline display
          .map(place => ({
            ...place, // Spread the entire PlaceResult
            placeId: place.place_id!, // Set placeId from place_id
            photos: place.photos?.map((photo, photoIndex) => ({
              ...photo, // Spread the entire PlacePhoto
              id: `${place.place_id}-photo-${photoIndex}`,
              url: photo.getUrl({ maxWidth: 400, maxHeight: 400 }),
              photoReference: (photo as any).photo_reference || `photo-${photoIndex}`,
              attribution: photo.html_attributions?.[0]
            })) || []
          }));

        setPlacesSearchResults(places);
        setShowPlacesResults(true);
        
        // Track successful search
        MapAnalytics.search(query, places.length);
      } else {
        setPlacesSearchResults([]);
        setShowPlacesResults(false);
        
        // Track search with no results
        trackNoResults(query, 'places_search');
      }
    });
  }, [userGeolocation]);

  // Handle places search input change
  const handlePlacesSearchChange = useCallback((value: string) => {
    setPlacesSearchText(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setPlacesSearchResults([]);
      setShowPlacesResults(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchPlaces(value);
    }, 300);
  }, [searchPlaces]);

  // Handle place selection from search results
  const handlePlaceSelectFromSearch = useCallback((place: CustomPlace) => {
    setCurrentActivity(place as Activity);
    setDrawerOpen(true);
    setShowPlacesResults(false);
    setPlacesSearchText('');

    // Center map on selected place
    if (mapRef.current && place.geometry?.location) {
      const { lat, lng } = getLatLng(place);
      mapRef.current.panTo({ lat, lng });
    }
  }, []);

  // Close places search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showPlacesResults && !target.closest('.search-overlay') && !target.closest('.places-results')) {
        setShowPlacesResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPlacesResults]);

  return (
    <div
      data-testid="google-map-container"
      className={`map-wrapper ${searchMode === SearchMode.AI && showSidebar && aiGeneratedLocations.length > 0 ? 'with-sidebar' : ''}`}
      style={{ width: '100%', height: '100%' }}
    >
      <BaseMap
        center={userGeolocation || DEFAULT_LOCATION}
        zoom={MAP_DEFAULT_ZOOM}
        onMapLoad={onMapLoad}
        showUserLocation={true}
        className={`${searchMode === SearchMode.AI && showSidebar && aiGeneratedLocations.length > 0 ? 'map-with-sidebar' : ''}`}
      >
        {/* Regular filtered markers - only show in Places mode */}
        {searchMode === SearchMode.Places && fetchMarkersQuery.data && fetchFilteredMarkers().map((place) => (
          <MemoizedMapMarker
            key={`memo-marker-${place.placeId}`}
            place={place}
            onClick={() => handleMarkerClick(place)}
          />
        ))}

        {/* AI-generated location markers - only show in AI mode */}
        {searchMode === SearchMode.AI && aiGeneratedLocations.map((location) => (
          <MapMarker
            key={`ai-marker-${location.placeId}`}
            isUser={false}
            customPlace={location}
            onClick={() => handleLocationSelect(location)}
          />
        ))}

        {/* Plan activity markers and paths - show in both modes */}
        {mapsClient && selectedPlan.activities.map((activity, idx) => (
          <div key={activity.placeId}>
            <MapMarker
              isUser={false}
              customPlace={activity}
              onClick={() => handleMarkerClick(activity)}
            />
            <Path
              key={`path-${activity.placeId}`}
              originCoords={idx === 0 ? (userGeolocation || DEFAULT_LOCATION) : getLatLng(selectedPlan.activities[idx - 1])}
              destinationCoords={getLatLng(activity)}
              isActive={true}
              mapsClient={mapsClient}
            />
          </div>
        ))}

        {/* AI-generated lines - only show in AI mode */}
        {searchMode === SearchMode.AI && mapsClient && aiGeneratedLines.map((line, idx) => {
          const startLocation = aiGeneratedLocations.find(loc => loc.placeId === line.start_location_id);
          const endLocation = aiGeneratedLocations.find(loc => loc.placeId === line.end_location_id);

          if (!startLocation || !endLocation) {
            return null;
          }

          return (
            <Path
              key={`ai-path-${line.id || idx}`}
              originCoords={getLatLng(startLocation)}
              destinationCoords={getLatLng(endLocation)}
              isActive={false}
              mapsClient={mapsClient}
            />
          );
        })}
      </BaseMap>

      {/* AI Mode Overlays */}
      {searchMode === SearchMode.AI && (
        <>
          {/* Card carousel for AI locations - only show when sidebar is not active */}
          {aiGeneratedLocations.length > 0 && !showSidebar && (
            <Box
              ref={cardContainerRef}
              className={`card-carousel-overlay ${showTimeline && isDesktopView ? 'card-carousel-shifted' : ''}`}
              sx={{
                position: 'absolute',
                bottom: 20,
                left: 20,
                right: 20,
                zIndex: 1000,
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                p: 1,
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0,0,0,0.1)',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: 4,
                },
              }}
            >
              {aiGeneratedLocations.map((location) => (
                <LocationCard
                  key={location.placeId}
                  location={location}
                  isActive={activeLocationId === location.placeId}
                  onClick={() => handleLocationSelect(location)}
                />
              ))}
            </Box>
          )}

          {/* Reset button for AI mode */}
          <IconButton
            onClick={handleResetAI}
            className="reset-map-button"
            disabled={isLoading || isStreaming}
            title="Reset AI Plan"
            sx={{
              position: 'absolute',
              bottom: 20,
              right: 20,
              zIndex: 1001,
              bgcolor: 'error.main',
              color: 'error.contrastText',
              '&:hover': {
                bgcolor: 'error.dark',
              }
            }}
          >
            <FontAwesomeIcon icon={faUndo} />
          </IconButton>


          {/* AI Plan Sidebar */}
          {searchMode === SearchMode.AI && (
            <MapSidebar
              isVisible={showSidebar && aiGeneratedLocations.length > 0}
              onClose={() => setShowSidebar(false)}
              itineraryItems={[...aiGeneratedLocations].sort((a, b) => (a.sequence || Infinity) - (b.sequence || Infinity))}
              connectingLines={aiGeneratedLines}
              activeItemId={activeLocationId}
              onItemClick={(item: PlannerMapPlace) => handleLocationSelect(item)}
              onExport={() => console.log('Export AI plan')}
              onSavePlan={() => console.log('Save AI plan')}
              timelineScrollAreaRef={timelineScrollAreaRef}
            />
          )}


          {/* Loading overlay */}
          {(isLoading && !isStreaming) && (
            <Box
              className="global-spinner-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.8)',
                zIndex: 2000,
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </>
      )}

      {/* Unified Search Bar - Always Visible */}
      <Box
        className={[
          'search-overlay',
          searchMode === SearchMode.AI && showSidebar && aiGeneratedLocations.length > 0 ? 'search-overlay-with-sidebar' : ''
        ].filter(Boolean).join(' ').trim()}
      >
        {searchMode === SearchMode.AI ? (
          // AI Mode: Direct prompt input
          <>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Ask AI to plan your day..."
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading && !isStreaming) {
                  handleSendPrompt();
                }
              }}
              disabled={isLoading || isStreaming}
              size="small"
              sx={{ flexGrow: 1 }}
            />
            <IconButton
              onClick={handleSendPrompt}
              disabled={isLoading || isStreaming || !promptText.trim()}
              color="primary"
              size="small"
            >
              {isLoading || isStreaming ? <CircularProgress size={20} /> : <FontAwesomeIcon icon={faSearch} />}
            </IconButton>
          </>
        ) : (
          // Places Mode: Inline places search
          <>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search for places..."
              value={placesSearchText}
              onChange={(e) => handlePlacesSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setPlacesSearchText('');
                  setShowPlacesResults(false);
                }
              }}
              size="small"
              sx={{ flexGrow: 1 }}
            />
            {placesSearchText && (
              <IconButton
                onClick={() => {
                  setPlacesSearchText('');
                  setShowPlacesResults(false);
                }}
                size="small"
                sx={{ ml: 1 }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </IconButton>
            )}
            {isPlacesSearching && (
              <CircularProgress size={20} sx={{ ml: 1 }} />
            )}
          </>
        )}
      </Box>

      {/* Places search results dropdown */}
      {searchMode === SearchMode.Places && showPlacesResults && placesSearchResults.length > 0 && (
        <Box
          className="places-results"
        >
          {placesSearchResults.map((place) => (
            <Box
              key={place.placeId}
              onClick={() => handlePlaceSelectFromSearch(place)}
              sx={{
                p: 2,
                cursor: 'pointer',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                '&:last-child': {
                  borderBottom: 'none',
                }
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {place.name}
              </Typography>
              {place.formatted_address && (
                <Typography variant="body2" color="text.secondary">
                  {place.formatted_address}
                </Typography>
              )}
              {place.rating && (
                <Typography variant="body2" color="text.secondary">
                  ⭐ {place.rating}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Clear AI results button - only show when AI mode is active and there are results */}
      {searchMode === SearchMode.AI && (aiGeneratedLocations.length > 0 || aiGeneratedLines.length > 0) && !isDesktopView && (
        <IconButton
          className="clear-ai-results-button"
          onClick={clearAIResults}
          sx={{
            position: 'absolute',
            bottom: 140,
            right: 20,
            zIndex: 1000,
            bgcolor: 'error.main',
            color: 'error.contrastText',
            '&:hover': {
              bgcolor: 'error.dark',
            }
          }}
          title="Clear AI Results"
        >
          <FontAwesomeIcon icon={faPlus} style={{ transform: 'rotate(45deg)' }} />
        </IconButton>
      )}

      {/* Search mode toggle */}
      {/* <SearchModeToggle
        mode={searchMode}
        onModeChange={handleSearchModeChange}
      /> */}

      {/* Full AI Planner button - only show when AI mode is active */}
      {searchMode === SearchMode.AI && (
        <Tooltip title="Open Full AI Planner">
          <IconButton
            className="full-ai-planner-button"
            onClick={() => console.log('/find')}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1000,
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              '&:hover': {
                bgcolor: 'secondary.dark',
              }
            }}
          >
            <FontAwesomeIcon icon={faWandMagicSparkles} />
          </IconButton>
        </Tooltip>
      )}

      {/* Filter panel - only show in Places mode */}
      {searchMode === SearchMode.Places && (
        <FilterPanel
          filters={filters}
          onChange={(newFilters) => setFilters(newFilters)}
          isOpen={isFilterOpen}
          onToggle={() => setIsFilterOpen(!isFilterOpen)}
        />
      )}

      <ActivityDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
        selectedActivity={currentActivity as Activity}
      />

      {/* Plan stepper - show in both modes */}
      {isCollapsed ? (
        <button className="plan-stepper-button-topright glassmorphic-button animate-hover" onClick={() => setIsCollapsed(!isCollapsed)} data-testid="plan-button">
          {selectedPlan.name} ({selectedPlan.activities.length})
        </button>
      ) : (
        <PlanStepperBase
          headerComponent={<PlanStepperHeader collapseHandler={() => setIsCollapsed(!isCollapsed)} />}
          onActivityClick={(activity) => {
            setCurrentActivity(activity as Activity)
            setDrawerOpen(true)
          }}
        />
      )}
    </div>
  );
};

export default Map;
